import { actions } from '@ecosystem/newron-design-system';
import React, { Dispatch, SetStateAction } from 'react';
import { useProductsModelCompare } from '../products-compare-modal/ProductsModelCompareContext';
import ActionBar from '../action-bar/ActionBar';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useProductAnalyzerGroupEventState } from '../top-selling-products/product-table-grouping/utils/ProductAnalyzerGroupContext';
import { useProductAnalyzerGroupState } from '@utils/hook/userPreferences/UseGroupState';
import {
  ADD_PGS_ELEMENT_NAME,
  GROUP_ELEMENT_TYPE,
  GroupContext,
  MODEL_GROUP_MAX_SELECTION,
  MODEL_GROUP_PGS_LIMIT_MESSAGE,
  PreferenceResponseStatus,
  PRODUCT_GROUP_MAX_SELECTION,
  totalNumberSelectedForAddingItems,
} from '@utils/userPreferencesUtils/Types';
import {
  PRODUCT_LEADERBOARD_TABLE_ELEMENT_CLICKED_TITLE,
  PRODUCT_LEADERBOARD_TABLE_NAME,
  TableElementClickedParam,
  groupElementClickedTrackingFunction,
} from '@utils/page-analytics/model/GroupElementClickTracking';
import { PageName, PageType } from '@utils/pageConfig/Types';
import { useSelector } from 'react-redux';
import { IRootState } from '@state/reducers/Reducers';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';

interface Props {
  totalElements: number;
  onClickCompare: () => void;
  context: GroupContext;
  isEditable: boolean;
  setIsEditable: Dispatch<SetStateAction<boolean>>;
}

const ProductsActionBar: React.FC<Props> = ({ totalElements, onClickCompare, context, isEditable, setIsEditable }) => {
  const { selectedModels, clearSelectedModels } = useProductsModelCompare();
  const numberOfSelectedModelsMessage: string = `${selectedModels.length} of ${totalElements} model(s) selected`;
  const compareInfoMessage: string = 'Compare up to 10 or make a group of up to 150 models';
  const noModelSelected: boolean = selectedModels.length === 0;
  const shouldShowCompareInfoMessage: boolean = selectedModels.length === 1 || selectedModels.length > 10;
  const shouldShowCompareButton: boolean = selectedModels.length >= 1;
  const sideFiltersState = useSelector((state: IRootState) => state.sideFilters);

  const { track } = useAnalytics();
  const handleCompare = () => {
    // This is temporary, will enhance this after getting feedback
    track('Compare models button clicked!');
    onClickCompare();
  };

  const {
    selectedModelGroups,
    selectedModelsName,
    setIsCreateModalGroupPopUp,
    isEditGroupMode,
    setIsEditGroupMode,
    clearSelectedModelsGroup,
  } = useProductAnalyzerGroupEventState();

  const { updateProductAnalyzerGroup } = useProductAnalyzerGroupState(context);
  const totalSelectedModelGroupsInEditMode = selectedModelGroups.length + isEditGroupMode.groupDetails.itemGroup.length;
  const atLeastOneMgSelected: boolean = selectedModelGroups.length >= 1;
  const isGroupPgsExceededInEditMode: boolean = totalSelectedModelGroupsInEditMode > MODEL_GROUP_MAX_SELECTION;
  const pagesConfig = usePageConfig();

  const handleGroup = () => {
    // This is temporary, will enhance this after getting feedback
    track('Group models button clicked!');
    setIsCreateModalGroupPopUp(true);
  };

  const handleCancelEditMode = () => {
    setIsEditGroupMode({
      groupDetails: { groupName: '', itemGroup: [], sortOrder: undefined },
      isEditMode: false,
    });
    clearSelectedModels();
    clearSelectedModelsGroup();
  };

  if (isEditable) {
    setIsEditGroupMode({
      groupDetails: { groupName: '', itemGroup: [], sortOrder: undefined },
      isEditMode: false,
    });
    setIsEditable(false);
    clearSelectedModels();
    clearSelectedModelsGroup();
  }

  const handleUpdateGroups = () => {
    const updatedProductGroups: string[] = [...isEditGroupMode.groupDetails.itemGroup, ...selectedModelGroups];

    //Amplitude Tracking when Add product group(s) button is click
    const trackingParam: TableElementClickedParam = {
      eventTitle: PRODUCT_LEADERBOARD_TABLE_ELEMENT_CLICKED_TITLE,
      elementName: ADD_PGS_ELEMENT_NAME.model,
      elementType: GROUP_ELEMENT_TYPE.model,
      track: track,
      optionChosen: selectedModelsName.flatMap((selectedMg) => selectedMg).join(', '),
      pageName: PageName.RI_PRODUCT,
      pageType: PageType.RI_PRODUCT,
      sideFilters: sideFiltersState.appliedFilters,
      tableName: PRODUCT_LEADERBOARD_TABLE_NAME,
      access: pagesConfig.access,
    };

    groupElementClickedTrackingFunction(trackingParam);

    updateProductAnalyzerGroup.mutate(
      {
        key: isEditGroupMode.groupDetails.groupName,
        data: {
          modalGroup: updatedProductGroups,
          sortOrder: isEditGroupMode.groupDetails.sortOrder!,
        },
      },
      {
        onSuccess: (response) => {
          if (response.status === PreferenceResponseStatus.SUCCESS) {
            clearSelectedModels();
            handleCancelEditMode();
            clearSelectedModelsGroup();
          }
        },
      },
    );
  };
  const { Button } = actions;
  if (isEditGroupMode.isEditMode) {
    return (
      <ActionBar
        isHidden={!isEditGroupMode.isEditMode}
        leftMessage={totalNumberSelectedForAddingItems(
          isEditGroupMode.groupDetails.groupName,
          totalSelectedModelGroupsInEditMode,
          MODEL_GROUP_MAX_SELECTION,
        )}
        centerMessage={isGroupPgsExceededInEditMode ? MODEL_GROUP_PGS_LIMIT_MESSAGE : ''}
      >
        {atLeastOneMgSelected && (
          <Button
            testId="product-action-bar-add-to-group"
            disabled={isGroupPgsExceededInEditMode}
            onClick={handleUpdateGroups}
          >
            Add to group
          </Button>
        )}
        <Button testId="product-action-bar-cancel" mode="secondary" onClick={handleCancelEditMode}>
          Cancel
        </Button>
      </ActionBar>
    );
  }

  return (
    <ActionBar
      leftMessage={numberOfSelectedModelsMessage}
      {...(shouldShowCompareInfoMessage && { centerMessage: compareInfoMessage })}
      {...(noModelSelected && { rightMessage: compareInfoMessage })}
    >
      {shouldShowCompareButton && (
        <div>
          <Button
            testId="product-action-bar-compare"
            onClick={handleCompare}
            disabled={shouldShowCompareInfoMessage}
          >
            Compare
          </Button>
          <Button
            testId="product-action-bar-group"
            onClick={handleGroup}
            disabled={selectedModelGroups.length > PRODUCT_GROUP_MAX_SELECTION}
          >
            Group
          </Button>
        </div>
      )}
    </ActionBar>
  );
};

export default ProductsActionBar;
